import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';

import { useActualizarInterpretacion } from '../hooks/useActualizarInterpretacion';

interface InterpretacionDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  numeroProtocolo: number;
  interpretacionActual: string | null;
}

function normalizeHtml(html: string): string | null {
  const t = html.trim();
  if (!t || t === '<p></p>') return null;
  return t;
}

type ConfirmKind = 'cancelar' | 'descartar' | null;

export function InterpretacionDialog({
  open,
  onClose,
  uidProtocolo,
  numeroProtocolo,
  interpretacionActual,
}: InterpretacionDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();
  const mutation = useActualizarInterpretacion();

  const [htmlValue, setHtmlValue] = useState(interpretacionActual ?? '');
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [saveError, setSaveError] = useState<unknown>(null);

  const effectiveHtml = normalizeHtml(htmlValue);
  const isDirty = effectiveHtml !== interpretacionActual;
  const isConflict = isApiError(saveError) && saveError.isConflict();

  function handleCancel(): void {
    if (isDirty) {
      setConfirmKind('cancelar');
    } else {
      mutation.reset();
      onClose();
    }
  }

  function handleConfirmDiscard(): void {
    setConfirmKind(null);
    mutation.reset();
    onClose();
  }

  async function handleDescartar(): Promise<void> {
    setConfirmKind(null);
    setSaveError(null);
    try {
      await mutation.mutateAsync({ uidProtocolo, interpretacion_resultados: null });
      notify.success('Interpretación eliminada.');
      onClose();
    } catch (err) {
      setSaveError(err);
    }
  }

  async function handleGuardar(): Promise<void> {
    setSaveError(null);
    try {
      await mutation.mutateAsync({
        uidProtocolo,
        interpretacion_resultados: effectiveHtml,
      });
      notify.success('Interpretación guardada.');
      onClose();
    } catch (err) {
      setSaveError(err);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={mutation.isPending ? undefined : handleCancel}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Interpretación de resultados
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Protocolo N° {numeroProtocolo}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <RichTextEditor
            value={interpretacionActual ?? ''}
            onChange={setHtmlValue}
            placeholder="Escribí la interpretación de los resultados..."
            disabled={mutation.isPending}
          />

          {saveError != null && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {isConflict
                ? 'Este protocolo está cerrado y no puede modificarse.'
                : getErrorMessage(saveError)}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          {interpretacionActual && (
            <Button
              variant="outlined"
              color="error"
              disabled={mutation.isPending}
              onClick={() => setConfirmKind('descartar')}
              sx={{ mr: 'auto' }}
            >
              Descartar contenido
            </Button>
          )}

          {isConflict ? (
            <Button onClick={onClose}>Volver</Button>
          ) : (
            <>
              <Button onClick={handleCancel} disabled={mutation.isPending}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                disabled={!isDirty || mutation.isPending}
                onClick={() => void handleGuardar()}
                startIcon={
                  mutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {mutation.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmar cancelar con cambios sin guardar */}
      <Dialog
        open={confirmKind === 'cancelar'}
        onClose={() => setConfirmKind(null)}
        maxWidth="xs"
      >
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tenés cambios sin guardar. ¿Cerrar sin guardar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmKind(null)}>Seguir editando</Button>
          <Button color="error" onClick={handleConfirmDiscard}>
            Cerrar sin guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminar interpretación */}
      <Dialog
        open={confirmKind === 'descartar'}
        onClose={() => setConfirmKind(null)}
        maxWidth="xs"
      >
        <DialogTitle>Eliminar interpretación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar la interpretación? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmKind(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={mutation.isPending}
            onClick={() => void handleDescartar()}
          >
            {mutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
